// src/pages/analysis/AnalysisSelfRegistrationForm.jsx
//
// SELF_REGISTRATION_FRONTEND_2 — self-registration ("1-day Basic trial")
// form rendered inside AnalysisLogin.jsx's login panel.
//
// This component:
//   - creates the Firebase account with createUserWithEmailAndPassword,
//   - then bootstraps the Basic trial via bootstrapSelfRegistrationTrial,
//   - and only calls onRegistrationComplete(user) after BOTH the
//     bootstrap call and its internal force-refreshed getIdToken(true)
//     have succeeded.
//
// It never imports/changes the Firebase config, never uses the
// Firebase Admin SDK, never touches the Firestore trial collection,
// never calls deleteUser, and never signs the user out automatically
// (Firebase Auth is shared across browser tabs).
import { useState, useRef } from "react";
import { createUserWithEmailAndPassword } from "firebase/auth";
import {
  bootstrapSelfRegistrationTrial,
  SelfRegistrationBootstrapError,
} from "./analysisSelfRegistrationClient";

const TEXT = {
  vi: {
    title: "Đăng ký dùng thử",
    subtitle: "Tạo tài khoản để dùng thử gói Basic trong 24 giờ.",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Mật khẩu",
    confirmPlaceholder: "Xác nhận mật khẩu",
    showPassword: "Hiện mật khẩu",
    hidePassword: "Ẩn mật khẩu",
    showConfirmPassword: "Hiện mật khẩu xác nhận",
    hideConfirmPassword: "Ẩn mật khẩu xác nhận",
    submitIdle: "Đăng ký dùng thử 1 ngày",
    submitLoading: "Đang xử lý...",
    backToLogin: "Đã có tài khoản? Đăng nhập",
    retryIdle: "Thử kích hoạt lại",
    retryLoading: "Đang kích hoạt...",
    retrySessionIdle: "Thử hoàn tất đăng nhập",
    retrySessionLoading: "Đang hoàn tất...",
    recoverNotice: "Tài khoản của bạn đã được tạo. Hãy kích hoạt gói dùng thử.",
    errors: {
      emailRequired: "Vui lòng nhập email.",
      passwordTooShort: "Mật khẩu phải có ít nhất 6 ký tự.",
      passwordMismatch: "Xác nhận mật khẩu không khớp.",
      "auth/email-already-in-use": "Email này đã được đăng ký. Vui lòng đăng nhập.",
      "auth/invalid-email": "Email không hợp lệ.",
      "auth/weak-password": "Mật khẩu phải có ít nhất 6 ký tự.",
      "auth/too-many-requests": "Bạn thao tác quá nhiều lần. Vui lòng thử lại sau.",
      "auth/network-request-failed": "Không thể kết nối. Vui lòng kiểm tra mạng.",
      trial_already_used: "Email hoặc tài khoản này đã sử dụng gói dùng thử.",
      account_disabled: "Tài khoản đã bị quản trị viên khóa.",
      account_not_found: "Không tìm thấy tài khoản.",
      trial_registry_conflict: "Không thể xác minh gói dùng thử. Vui lòng liên hệ hỗ trợ.",
      bootstrap5xx: "Tài khoản đã được tạo nhưng chưa thể kích hoạt gói dùng thử. Hãy thử kích hoạt lại.",
      sessionCompletionFailed:
        "Gói dùng thử đã được kích hoạt nhưng chưa thể hoàn tất đăng nhập. Vui lòng thử lại.",
      generic: "Đã xảy ra lỗi. Vui lòng thử lại.",
    },
  },
  en: {
    title: "Sign up for a trial",
    subtitle: "Create an account to try the Basic plan free for 24 hours.",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    confirmPlaceholder: "Confirm password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    showConfirmPassword: "Show confirmation password",
    hideConfirmPassword: "Hide confirmation password",
    submitIdle: "Register for 1-day trial",
    submitLoading: "Processing...",
    backToLogin: "Already have an account? Sign in",
    retryIdle: "Try activating again",
    retryLoading: "Activating...",
    retrySessionIdle: "Try completing sign-in",
    retrySessionLoading: "Completing...",
    recoverNotice: "Your account has been created. Please activate your trial.",
    errors: {
      emailRequired: "Please enter your email.",
      passwordTooShort: "Password must be at least 6 characters.",
      passwordMismatch: "Passwords do not match.",
      "auth/email-already-in-use": "This email is already registered. Please sign in.",
      "auth/invalid-email": "Invalid email address.",
      "auth/weak-password": "Password must be at least 6 characters.",
      "auth/too-many-requests": "Too many attempts. Please try again later.",
      "auth/network-request-failed": "Could not connect. Please check your network.",
      trial_already_used: "This email or account has already used the trial.",
      account_disabled: "This account has been disabled by an administrator.",
      account_not_found: "Account not found.",
      trial_registry_conflict: "Could not verify the trial. Please contact support.",
      bootstrap5xx: "Your account was created, but the trial could not be activated yet. Please try activating again.",
      sessionCompletionFailed: "Your trial was activated, but sign-in could not be completed. Please try again.",
      generic: "Something went wrong. Please try again.",
    },
  },
};

function resolveText(lang) {
  const code = String(lang || "").trim().toLowerCase();
  return code === "vi" || code === "vi-vn" ? TEXT.vi : TEXT.en;
}

function mapFirebaseAuthError(code, t) {
  return t.errors[code] || t.errors.generic;
}

function mapBootstrapError(error, t) {
  const code = error && error.code;
  if (code && t.errors[code]) return t.errors[code];
  const status = error && typeof error.status === "number" ? error.status : 0;
  if (status === 0 || status >= 500) return t.errors.bootstrap5xx;
  return t.errors.generic;
}

// Small inline icon helpers for the password visibility toggles. No
// external icon library, no emoji, no images — plain SVG using
// currentColor so it inherits the toggle button's color/hover/disabled
// states.
function EyeIcon() {
  return (
    <svg
      className="analysis-login-password-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      className="analysis-login-password-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M3 3l18 18" />
      <path d="M10.58 10.58a3 3 0 0 0 4.24 4.24" />
      <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c7 0 10.5 7 10.5 7a13.16 13.16 0 0 1-2.16 3.02M6.61 6.61C3.9 8.36 1.5 12 1.5 12S5 19 12 19a10.9 10.9 0 0 0 3.39-.56" />
    </svg>
  );
}

export default function AnalysisSelfRegistrationForm({ auth, lang, onRegistrationComplete, onBackToLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdUser, setCreatedUser] = useState(null);
  // "bootstrap": trial bootstrap has not yet succeeded for this account —
  // retry must call bootstrapSelfRegistrationTrial again.
  // "session": bootstrap already succeeded — retry must only call
  // onRegistrationComplete again, never bootstrap or create the account again.
  const [completionStage, setCompletionStage] = useState("bootstrap");

  const submittingRef = useRef(false);

  const t = resolveText(lang);

  function validate() {
    const trimmedEmail = String(email || "").trim();
    if (!trimmedEmail) return t.errors.emailRequired;
    if (String(password ?? "").length < 6) return t.errors.passwordTooShort;
    if (password !== confirmPassword) return t.errors.passwordMismatch;
    return "";
  }

  // Stage 2 only: calls onRegistrationComplete(user) and keeps the
  // double-submit guard held for the ENTIRE duration of that call. The
  // guard is only released in the finally block below, i.e. after the
  // callback has resolved or rejected — never before.
  async function completeSession(user) {
    try {
      if (typeof onRegistrationComplete === "function") {
        await onRegistrationComplete(user);
      }
    } catch (completionErr) {
      // A session-finalization failure is never conflated with a
      // bootstrap failure: the trial IS already active, so this must
      // never show "trial not activated".
      setCompletionStage("session");
      setErr(t.errors.sessionCompletionFailed);
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  }

  // Stage 1 + 2: bootstraps the trial, then (only on bootstrap success)
  // proceeds straight into completeSession without ever releasing the
  // double-submit guard in between.
  async function bootstrapThenCompleteSession(user) {
    try {
      await bootstrapSelfRegistrationTrial(user);
    } catch (bootstrapErr) {
      setCompletionStage("bootstrap");
      setErr(
        bootstrapErr instanceof SelfRegistrationBootstrapError
          ? mapBootstrapError(bootstrapErr, t)
          : t.errors.bootstrap5xx
      );
      setLoading(false);
      submittingRef.current = false;
      return;
    }

    setCompletionStage("session");
    await completeSession(user);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submittingRef.current || loading) return;

    const validationError = validate();
    if (validationError) {
      setErr(validationError);
      return;
    }

    submittingRef.current = true;
    setErr("");
    setLoading(true);
    setCompletionStage("bootstrap");

    const trimmedEmail = String(email || "").trim();

    let user;
    try {
      if (!auth) {
        throw new Error("firebase_auth_unavailable");
      }
      const cred = await createUserWithEmailAndPassword(auth, trimmedEmail, password);
      user = cred.user;
      setCreatedUser(user);
    } catch (createErr) {
      const code = createErr && createErr.code;
      setErr(code ? mapFirebaseAuthError(code, t) : t.errors.generic);
      setLoading(false);
      submittingRef.current = false;
      return;
    }

    await bootstrapThenCompleteSession(user);
  }

  async function handleRetry() {
    if (submittingRef.current || loading || !createdUser) return;

    submittingRef.current = true;
    setErr("");
    setLoading(true);

    if (completionStage === "session") {
      // Bootstrap already succeeded earlier — never re-bootstrap, never
      // re-create the Firebase account, just retry session finalization.
      await completeSession(createdUser);
    } else {
      await bootstrapThenCompleteSession(createdUser);
    }
  }

  if (createdUser) {
    return (
      <div className="analysis-login-form">
        <div className="analysis-login-subtitle">{t.recoverNotice}</div>

        {err && (
          <div className="analysis-login-error" role="alert">
            {err}
          </div>
        )}

        <button
          type="button"
          disabled={loading}
          className="analysis-login-button"
          onClick={handleRetry}
        >
          {completionStage === "session"
            ? (loading ? t.retrySessionLoading : t.retrySessionIdle)
            : (loading ? t.retryLoading : t.retryIdle)}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="analysis-login-form">
      <input
        className="analysis-login-input"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder={t.emailPlaceholder}
        autoComplete="email"
        disabled={loading}
      />

      <div className="analysis-login-password-field">
        <input
          className="analysis-login-input"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t.passwordPlaceholder}
          autoComplete="new-password"
          disabled={loading}
        />
        <button
          type="button"
          className="analysis-login-password-toggle"
          aria-pressed={showPassword}
          aria-label={showPassword ? t.hidePassword : t.showPassword}
          title={showPassword ? t.hidePassword : t.showPassword}
          disabled={loading}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowPassword((v) => !v)}
        >
          {showPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      <div className="analysis-login-password-field">
        <input
          className="analysis-login-input"
          type={showConfirmPassword ? "text" : "password"}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder={t.confirmPlaceholder}
          autoComplete="new-password"
          disabled={loading}
        />
        <button
          type="button"
          className="analysis-login-password-toggle"
          aria-pressed={showConfirmPassword}
          aria-label={showConfirmPassword ? t.hideConfirmPassword : t.showConfirmPassword}
          title={showConfirmPassword ? t.hideConfirmPassword : t.showConfirmPassword}
          disabled={loading}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setShowConfirmPassword((v) => !v)}
        >
          {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
        </button>
      </div>

      {err && (
        <div className="analysis-login-error" role="alert">
          {err}
        </div>
      )}

      <button type="submit" disabled={loading} className="analysis-login-button">
        {loading ? t.submitLoading : t.submitIdle}
      </button>

      <button
        type="button"
        className="analysis-login-panel-close"
        disabled={loading}
        onClick={onBackToLogin}
      >
        {t.backToLogin}
      </button>
    </form>
  );
}
